import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

/**
 * SPECIAL MOMENTS — Editorial Cinematic Scroll
 *
 * Concept: a fashion-magazine spread, not a gallery grid.
 * - Alternating left/right layout per moment.
 * - Image reveals with a horizontal clip-path "wipe" + slow zoom-out
 *   on enter (so every image feels lensed, not just faded).
 * - Couture-style numbering (N°01 → N°04).
 * - Text slides in from the side after the image lands.
 * - A vertical "MOMENTS" sidebar label parked on the left rail.
 * - On scroll, an editorial progress bar fills along the side.
 */

const MOMENTS = [
    {
        img: "/api/files/cake-central/bulk-import/cakes/wedding/wedding-01.jpg",
        number: "N°01",
        category: "Wedding",
        title: "Botanical Magnolia",
        story:
            "Hand-sculpted magnolia florals cascading across four bone-white tiers — a square architectural form softened by the most delicate of details. Designed for a Sydney ballroom under chandelier light.",
    },
    {
        img: "/api/files/cake-central/bulk-import/cakes/wedding/wedding-05.jpg",
        number: "N°02",
        category: "Wedding",
        title: "The Sculpted Tower",
        story:
            "Five tiers of pure ivory, finished with a hand-pulled sugar ribbon ascending like windswept silk. Modern minimalism executed with old-world patisserie technique.",
    },
    {
        img: "/api/files/cake-central/bulk-import/cakes/wedding/wedding-08.jpg",
        number: "N°03",
        category: "Hens",
        title: "A Riot of Bloom",
        story:
            "Vivid florals, hand-piped ribbon drips, and an electric pink-and-orange palette set against fresh orchids — built for a bride who refused to be subtle.",
    },
    {
        img: "/api/files/cake-central/bulk-import/cakes/wedding/wedding-10.jpg",
        number: "N°04",
        category: "Wedding",
        title: "Whisper Bouquet",
        story:
            "A two-tier classic, crowned with a cascading bouquet of fresh-cut blooms in cream and sage — intimate, restrained, unforgettable.",
    },
];

function MomentRow({ moment, index, total }) {
    const ref = useRef(null);
    const [visible, setVisible] = useState(false);
    const reversed = index % 2 === 1;

    useEffect(() => {
        const node = ref.current;
        if (!node) return;
        const r = node.getBoundingClientRect();
        if (r.top < window.innerHeight * 0.85 && r.bottom > 0) {
            setVisible(true);
            return;
        }
        const io = new IntersectionObserver(
            ([entry]) => entry.isIntersecting && setVisible(true),
            { threshold: 0.18, rootMargin: "0px 0px -10% 0px" }
        );
        io.observe(node);
        const safety = setTimeout(() => setVisible(true), 2200);
        return () => {
            io.disconnect();
            clearTimeout(safety);
        };
    }, []);

    return (
        <article
            ref={ref}
            className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-14 items-center py-14 md:py-24"
            data-testid={`moment-row-${index}`}
        >
            {/* IMAGE COLUMN */}
            <div className={`md:col-span-6 ${reversed ? "md:order-2 md:col-start-7" : ""} relative`}>
                <div className="relative overflow-hidden bg-blush-pale aspect-[4/5] md:aspect-[3/4] max-w-md md:max-w-lg mx-auto shadow-[0_40px_80px_-30px_rgba(236,0,140,0.35)]">
                    <img
                        src={moment.img}
                        alt={moment.title}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover object-top"
                        style={{
                            transform: visible ? "scale(1)" : "scale(1.2)",
                            clipPath: visible
                                ? "inset(0 0 0 0)"
                                : reversed
                                    ? "inset(0 0 0 100%)"
                                    : "inset(0 100% 0 0)",
                            transition:
                                "clip-path 1500ms cubic-bezier(0.77, 0, 0.175, 1), transform 2600ms cubic-bezier(0.16, 1, 0.3, 1)",
                        }}
                    />

                    {/* Decorative serif numeral over image */}
                    <div
                        aria-hidden
                        className={`absolute ${reversed ? "left-5 bottom-5 md:left-8 md:bottom-8" : "right-5 bottom-5 md:right-8 md:bottom-8"} font-display italic text-white/95 text-4xl md:text-6xl select-none pointer-events-none`}
                        style={{
                            textShadow: "0 2px 24px rgba(0,0,0,0.4)",
                            opacity: visible ? 1 : 0,
                            transition: "opacity 1000ms ease 900ms",
                        }}
                    >
                        {moment.number}
                    </div>

                    {/* Subtle bottom warmth */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-black/0 to-transparent pointer-events-none" />
                </div>

                {/* Caption strip below image, mobile-readable */}
                <div className="mt-4 md:mt-6 flex items-center gap-4 text-[10px] tracking-[0.45em] uppercase text-ink-soft">
                    <span className="text-blush font-medium">{moment.category}</span>
                    <span className="h-px flex-1 bg-blush/30" />
                    <span>{`${String(index + 1).padStart(2, "0")} / ${String(total).padStart(2, "0")}`}</span>
                </div>
            </div>

            {/* TEXT COLUMN */}
            <div className={`md:col-span-6 ${reversed ? "md:order-1 md:col-start-1 md:row-start-1 md:text-right md:pr-4" : "md:pl-4"}`}>
                <div
                    style={{
                        opacity: visible ? 1 : 0,
                        transform: visible ? "translateX(0)" : `translateX(${reversed ? 40 : -40}px)`,
                        transition: "opacity 1100ms ease 600ms, transform 1100ms cubic-bezier(0.22, 1, 0.36, 1) 600ms",
                    }}
                >
                    <p className="font-display italic text-blush text-2xl md:text-3xl mb-4">
                        {moment.number}
                    </p>
                    <h3 className="font-display text-3xl md:text-5xl lg:text-6xl text-ink leading-[1.0] mb-5">
                        {moment.title}
                    </h3>
                    <div className={`h-[2px] w-16 bg-blush mb-6 ${reversed ? "md:ml-auto" : ""}`} />
                    <p className="text-base md:text-lg text-ink-soft leading-relaxed max-w-md mb-7">
                        {moment.story}
                    </p>
                    <Link
                        to="/contact"
                        className={`group inline-flex items-center gap-3 text-[11px] tracking-[0.35em] uppercase text-ink hover:text-blush transition-colors ${reversed ? "md:flex-row-reverse" : ""}`}
                        data-testid={`moment-cta-${index}`}
                    >
                        <span>Enquire about a similar design</span>
                        <span className="w-8 h-8 border border-ink group-hover:border-blush group-hover:bg-blush group-hover:text-white flex items-center justify-center transition-all">
                            <ArrowUpRight size={12} />
                        </span>
                    </Link>
                </div>
            </div>
        </article>
    );
}

export default function SpecialMoments() {
    const sectionRef = useRef(null);
    const [progress, setProgress] = useState(0);
    const [headerVisible, setHeaderVisible] = useState(false);
    const headerRef = useRef(null);

    // Section scroll progress (for the editorial side progress bar)
    useEffect(() => {
        const node = sectionRef.current;
        if (!node) return;
        let raf;
        const onScroll = () => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => {
                const r = node.getBoundingClientRect();
                const total = r.height - window.innerHeight;
                const passed = Math.min(Math.max(-r.top, 0), total);
                setProgress(total > 0 ? passed / total : 0);
            });
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        onScroll();
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    // Header reveal
    useEffect(() => {
        const node = headerRef.current;
        if (!node) return;
        const r = node.getBoundingClientRect();
        if (r.top < window.innerHeight) {
            setHeaderVisible(true);
            return;
        }
        const io = new IntersectionObserver(
            ([entry]) => entry.isIntersecting && setHeaderVisible(true),
            { threshold: 0.3 }
        );
        io.observe(node);
        return () => io.disconnect();
    }, []);

    return (
        <section
            ref={sectionRef}
            className="relative bg-gradient-to-b from-white via-blush-pale/30 to-white overflow-hidden"
            data-testid="special-moments"
        >
            {/* Vertical sidebar label + progress bar (desktop) */}
            <div className="hidden lg:flex fixed top-1/2 left-6 -translate-y-1/2 z-20 flex-col items-center gap-6 pointer-events-none">
                <div className="text-[10px] tracking-[0.5em] uppercase text-blush rotate-180 font-medium" style={{ writingMode: "vertical-rl" }}>
                    Special · Moments
                </div>
                <div className="relative w-px h-32 bg-ink/15 overflow-hidden">
                    <div
                        className="absolute top-0 left-0 w-full bg-blush"
                        style={{ height: `${Math.round(progress * 100)}%`, transition: "height 200ms linear" }}
                    />
                </div>
                <div className="font-display italic text-ink-soft text-sm">
                    {String(Math.min(Math.ceil(progress * MOMENTS.length) || 1, MOMENTS.length)).padStart(2, "0")} <span className="text-blush">/</span> {String(MOMENTS.length).padStart(2, "0")}
                </div>
            </div>

            {/* Editorial header */}
            <div className="relative px-4 sm:px-6 lg:px-16 max-w-screen-2xl mx-auto pt-20 md:pt-28 pb-6">
                <div
                    ref={headerRef}
                    className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 md:gap-16 border-b border-blush/20 pb-10 md:pb-14"
                    style={{
                        opacity: headerVisible ? 1 : 0,
                        transform: headerVisible ? "translateY(0)" : "translateY(24px)",
                        transition: "opacity 900ms ease, transform 900ms ease",
                    }}
                >
                    <div className="max-w-2xl">
                        <p className="text-xs tracking-[0.5em] uppercase text-blush font-medium mb-5">
                            Special Moments · Volume 01
                        </p>
                        <h2 className="font-display text-4xl md:text-6xl lg:text-7xl text-ink leading-[0.98]">
                            The cakes behind<br />
                            the <span className="italic text-blush">celebrations</span>.
                        </h2>
                    </div>
                    <div className="md:max-w-sm md:text-right md:pt-6">
                        <p className="text-base md:text-lg text-ink-soft leading-relaxed">
                            Four moments. Four love stories. Each cake bespoke to the day,
                            hand-built in our Sydney studio — and trusted to be the
                            centrepiece of an unrepeatable evening.
                        </p>
                    </div>
                </div>
            </div>

            {/* Moments — alternating editorial spreads */}
            <div className="relative px-4 sm:px-6 lg:px-16 max-w-screen-2xl mx-auto">
                {MOMENTS.map((m, i) => (
                    <MomentRow key={i} moment={m} index={i} total={MOMENTS.length} />
                ))}
            </div>

            {/* Closing CTA — full-width band with editorial flourish */}
            <div className="relative mt-6 md:mt-10 mb-20 md:mb-28 px-4 sm:px-6 lg:px-16 max-w-screen-2xl mx-auto">
                <div className="border-t border-blush/20 pt-12 md:pt-16 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
                    <p className="font-display italic text-2xl md:text-4xl text-ink max-w-2xl leading-tight">
                        Your day deserves a cake built around <span className="text-blush">your</span> story.
                    </p>
                    <Link to="/contact" className="btn-pink self-start md:self-auto" data-testid="moments-cta">
                        Begin yours <ArrowUpRight size={14} />
                    </Link>
                </div>
            </div>
        </section>
    );
}
